import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createSavedView as createSavedViewApi,
  deleteSavedView as deleteSavedViewApi,
  listSavedViews as listSavedViewsApi,
  patchSavedView as patchSavedViewApi,
  type SavedViewResponse,
  type SavedViewScope,
} from '../api/savedViews'
import { ApiError } from '../api/problem'

/**
 * Saved Views / Filters — the "name and re-apply a list's current filter/sort/column
 * configuration" capability (CLAUDE.md §4 IA rule + every `ListView`-based list). IB-27 S1
 * (ADR-0058) cuts this over from a browser-local (`localStorage`) store to the server-persisted
 * `/api/v1/saved-views` endpoint — the server is now the single source of truth (no `localStorage`
 * shadow store; a failed save surfaces nowhere silent, never a hidden fallback, design §5 Q2).
 * `team-shared`/`tenant-default` scopes stay disclosed as "coming soon" in `SavedViewsMenu` — the
 * backend rejects them with a `422`, never a silent downgrade (RAID R-35).
 *
 * Generic over the caller's own `TState` shape exactly as before — this file never needs to know
 * about Accounts/Cases/Contacts/etc. Namespaced per `listId`. {@link useSavedViews}'s signature and
 * {@link SavedViewsController} shape are UNCHANGED from the pre-cutover version for every PERSONAL
 * (`user`-scope) field, so every `ListView`-based screen wiring this hook needed zero edits (design
 * §5, "behavior-invisible"). IB-27 S4a (ADR-0059) ADDS a `tenant`-scope (capability-gated on
 * `saved_view:A`, not ownership) alongside it — purely additive fields/callbacks below, so no
 * existing caller of this hook needs to change.
 */

export interface SavedView<TState> {
  id: string
  name: string
  isDefault: boolean
  createdAt: string
  updatedAt: string
  state: TState
}

/** The shape the `SavedViewsMenu` control needs — never the raw `state` blob. IB-27 S4a: `scope`
 * distinguishes a personal (`user`) row from the shared `tenant`-default. */
export interface SavedViewSummary {
  id: string
  name: string
  isDefault: boolean
  scope: SavedViewScope
}

export class SavedViewNameError extends Error {
  constructor() {
    super('A view name is required.')
    this.name = 'SavedViewNameError'
  }
}

function requireName(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) throw new SavedViewNameError()
  return trimmed
}

/** The internal representation carries `version` (needed for `If-Match` on PATCH/DELETE) — the
 * PUBLIC {@link SavedViewsController.views} summary never exposes it, matching the pre-cutover
 * shape exactly. */
interface InternalView<TState> extends SavedView<TState> {
  version: number
  scope: SavedViewScope
}

function fromResponse<TState>(r: SavedViewResponse): InternalView<TState> {
  return {
    id: r.id,
    name: r.name,
    isDefault: r.isDefault,
    createdAt: r.createdAt,
    updatedAt: r.updatedAt,
    state: r.state as TState,
    version: r.version,
    scope: r.scope,
  }
}

/** True for a 403 the backend's `ApiExceptionHandler` maps from a capability denial (RFC 9457
 * `type` slug `capability-denied`, e.g. `SavedViewService.SavedViewAccessDeniedException` — IB-27
 * S4a §6.2). Mirrors `CloneProgramDialog.friendlyCloneError`'s exact `status`-or-`slug` check. */
function isCapabilityDenied(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false
  const slug = err.problem.type?.split('/').pop()
  return err.status === 403 || slug === 'capability-denied'
}

/**
 * IB-27 S4a design §2 Q3 — deterministic, single-winner default-view precedence: a personal
 * (`user`-scope) default ALWAYS wins over a `tenant`-default for the same list, forward-compatible
 * with a future `team` rung between them (S4b, not built here). Pure function — exported for a
 * fast, dedicated Vitest unit test independent of the hook's async/effect plumbing.
 */
export function resolveDefaultView<T extends { isDefault: boolean; scope: SavedViewScope }>(
  rows: readonly T[],
): T | null {
  return rows.find((v) => v.isDefault && v.scope === 'user') ?? rows.find((v) => v.isDefault && v.scope === 'tenant') ?? null
}

// ---------------------------------------------------------------------------
// React hook — the per-screen wiring surface
// ---------------------------------------------------------------------------

export interface SavedViewsController {
  views: SavedViewSummary[]
  /** The saved view last applied (by click or as the auto-applied default) — `null` before any
   * view has been applied this session, or after the active one is deleted. */
  activeViewId: string | null
  /** True when a view is active and the live state no longer matches its saved state — drives the
   * menu's "unsaved changes" note. Always false when no view has been applied yet. */
  isDirty: boolean
  /** A user-facing message for the MOST RECENT mutating action's failure (save/rename/delete/
   * set-default/unset-default) — `null` when there is nothing to show. IB-27 S1 Verifier S3 fix:
   * a failed mutation used to be swallowed with `.catch(() => {})` and no visible signal; every
   * mutating action now sets this on failure (cleared at the start of the NEXT attempt) so
   * {@link SavedViewsMenu} can surface it via `InlineAlert` — still no `localStorage` fallback, the
   * write simply never lands and the user is told so. */
  error: string | null
  onApply: (id: string) => void
  onSaveNew: (name: string) => void
  onRename: (id: string, name: string) => void
  onDelete: (id: string) => void
  onSetDefault: (id: string) => void
  onUnsetDefault: () => void
  /** IB-27 S4a (ADR-0059): saves the CURRENT live state as the shared tenant-default for this list
   * (auto-marked `isDefault`) — capability-gated (`saved_view:A`) server-side, never ownership-
   * gated. Publishing REPLACES the prior tenant-default for this list (there is at most one). */
  onSaveNewTenantDefault: (name: string) => void
  /** True once a tenant-default mutation attempt has been denied `403` by the server THIS session —
   * the REAL gate (design §6.4: "the server 403 is the real gate, the UI hint must never be the
   * only gate"). `false`/unset means "not yet known to be denied," rendered as enabled — this flag
   * only ever narrows the affordance in reaction to an actual denial, never proactively guesses. */
  tenantDefaultCapabilityDenied: boolean
}

/**
 * Owns the CRUD + "apply the default view once on mount" wiring for one list's saved views, now
 * against the server instead of `localStorage`. Every mutating callback fires the request and
 * reconciles by RE-FETCHING the full list on success (rather than patching the in-memory array
 * itself) — deliberately simple and robust: a single-default "set" clears every OTHER row's
 * default server-side too (bumping ITS version), so a full reload is the only way this hook's
 * cached `version`s never go stale and cause a spurious `409` on a later action. Views per
 * (user, list) are always a small N, so the extra round trip is inexpensive. A failed mutation (or
 * the initial load) surfaces nowhere silent — it just leaves the prior state in place; there is no
 * localStorage fallback to quietly drop into (design §5 Q2, AC-SV1).
 */
export function useSavedViews<TState>(
  listId: string,
  currentState: TState,
  applyState: (state: TState) => void,
): SavedViewsController {
  const [views, setViews] = useState<InternalView<TState>[]>([])
  const [activeViewId, setActiveViewId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [tenantDefaultCapabilityDenied, setTenantDefaultCapabilityDenied] = useState(false)
  const appliedDefaultRef = useRef(false)
  const applyStateRef = useRef(applyState)
  applyStateRef.current = applyState
  const viewsRef = useRef<InternalView<TState>[]>([])

  const setViewsBoth = useCallback((next: InternalView<TState>[]) => {
    viewsRef.current = next
    setViews(next)
  }, [])

  const reload = useCallback(
    (signal?: AbortSignal) =>
      listSavedViewsApi(listId, signal).then((rows) => {
        const mapped = rows.map((r) => fromResponse<TState>(r))
        setViewsBoth(mapped)
        return mapped
      }),
    [listId, setViewsBoth],
  )

  useEffect(() => {
    const controller = new AbortController()
    reload(controller.signal)
      .then((mapped) => {
        if (appliedDefaultRef.current) return
        appliedDefaultRef.current = true
        // S4a design §2 Q3: personal default beats a tenant-default for the same list.
        const def = resolveDefaultView(mapped)
        if (def) {
          applyStateRef.current(def.state)
          setActiveViewId(def.id)
        }
      })
      .catch(() => {
        // A failed initial load degrades to "no saved views yet" — SavedViewsMenu already renders
        // that state gracefully; the screen itself (its live filter/sort/columns) is unaffected.
      })
    return () => controller.abort()
    // Deliberately listId-only, mirroring the pre-cutover mount-once-per-list contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId])

  const activeView = views.find((v) => v.id === activeViewId) ?? null
  const isDirty = activeView ? JSON.stringify(activeView.state) !== JSON.stringify(currentState) : false

  const onApply = useCallback((id: string) => {
    const target = viewsRef.current.find((v) => v.id === id)
    if (!target) return
    applyStateRef.current(target.state)
    setActiveViewId(id)
  }, [])

  const onSaveNew = useCallback(
    (name: string) => {
      let trimmed: string
      try {
        trimmed = requireName(name)
      } catch {
        return
      }
      setError(null)
      createSavedViewApi({ listId, name: trimmed, state: currentState })
        .then(({ data }) => {
          setActiveViewId(data.id)
          return reload()
        })
        .catch(() => {
          // Persistence failure surfaces nowhere silent — no localStorage fallback; the view
          // simply never appears, and the user is told so (IB-27 S1 Verifier S3).
          setError('Could not save this view. Please try again.')
        })
    },
    [listId, currentState, reload],
  )

  const onRename = useCallback(
    (id: string, name: string) => {
      let trimmed: string
      try {
        trimmed = requireName(name)
      } catch {
        return
      }
      const target = viewsRef.current.find((v) => v.id === id)
      if (!target) return
      setError(null)
      patchSavedViewApi(id, { name: trimmed }, target.version)
        .then(() => reload())
        .catch((err: unknown) => {
          if (target.scope === 'tenant' && isCapabilityDenied(err)) {
            setTenantDefaultCapabilityDenied(true)
            setError('You do not have permission to manage this firm-wide default view.')
            return
          }
          setError('Could not rename this view. Please try again.')
        })
    },
    [reload],
  )

  const onDelete = useCallback(
    (id: string) => {
      const target = viewsRef.current.find((v) => v.id === id)
      if (!target) return
      setError(null)
      deleteSavedViewApi(id, target.version)
        .then(() => {
          setActiveViewId((prev) => (prev === id ? null : prev))
          return reload()
        })
        .catch((err: unknown) => {
          if (target.scope === 'tenant' && isCapabilityDenied(err)) {
            setTenantDefaultCapabilityDenied(true)
            setError('You do not have permission to manage this firm-wide default view.')
            return
          }
          setError('Could not delete this view. Please try again.')
        })
    },
    [reload],
  )

  const onSetDefault = useCallback(
    (id: string) => {
      const target = viewsRef.current.find((v) => v.id === id)
      if (!target) return
      setError(null)
      patchSavedViewApi(id, { isDefault: true }, target.version)
        .then(() => reload())
        .catch((err: unknown) => {
          if (target.scope === 'tenant' && isCapabilityDenied(err)) {
            setTenantDefaultCapabilityDenied(true)
            setError('You do not have permission to manage this firm-wide default view.')
            return
          }
          setError('Could not set this view as default. Please try again.')
        })
    },
    [reload],
  )

  const onUnsetDefault = useCallback(() => {
    // Unchanged S1 personal-scope behavior (AC12) — deliberately scoped to `user` so a widened
    // list (S4a) that ALSO carries a `tenant`-default never lets this ambiguously target the
    // shared row instead of the caller's own personal default (there is no "unset the tenant-
    // default" affordance in S4a; a tenant-default is replaced by a new publish, never unset).
    const current = viewsRef.current.find((v) => v.isDefault && v.scope === 'user')
    if (!current) return
    setError(null)
    patchSavedViewApi(current.id, { isDefault: false }, current.version)
      .then(() => reload())
      .catch(() => setError('Could not unset this view as default. Please try again.'))
  }, [reload])

  const onSaveNewTenantDefault = useCallback(
    (name: string) => {
      let trimmed: string
      try {
        trimmed = requireName(name)
      } catch {
        return
      }
      setError(null)
      const onSuccess = (id: string) => {
        setTenantDefaultCapabilityDenied(false)
        setActiveViewId(id)
        return reload()
      }
      const onFailure = (err: unknown) => {
        if (isCapabilityDenied(err)) {
          // The REAL gate firing (design §6.4) — narrow the affordance from here on, not
          // proactively guessed. Still surfaces a message via the shared `error` channel exactly
          // like every other mutation failure (no silent swallow, IB-27 S1 Verifier S3 posture).
          setTenantDefaultCapabilityDenied(true)
          setError('You do not have permission to publish a tenant-default view for this firm.')
          return
        }
        setError('Could not save this tenant-default view. Please try again.')
      }
      // Republishing REPLACES the existing tenant-default in place (rename + re-state) rather than
      // leaving an orphaned non-default row behind — there is at most one tenant-default per list
      // (server-enforced, V44 saved_view_one_tenant_default_uidx); only the FIRST publish creates a
      // new row.
      const existingTenantDefault = viewsRef.current.find((v) => v.scope === 'tenant' && v.isDefault)
      if (existingTenantDefault) {
        patchSavedViewApi(
          existingTenantDefault.id,
          { name: trimmed, state: currentState, isDefault: true },
          existingTenantDefault.version,
        )
          .then(() => onSuccess(existingTenantDefault.id))
          .catch(onFailure)
        return
      }
      createSavedViewApi({ listId, name: trimmed, state: currentState, scope: 'tenant', isDefault: true })
        .then(({ data }) => onSuccess(data.id))
        .catch(onFailure)
    },
    [listId, currentState, reload],
  )

  return {
    views: views.map((v) => ({ id: v.id, name: v.name, isDefault: v.isDefault, scope: v.scope })),
    activeViewId,
    isDirty,
    error,
    onApply,
    onSaveNew,
    onRename,
    onDelete,
    onSetDefault,
    onUnsetDefault,
    onSaveNewTenantDefault,
    tenantDefaultCapabilityDenied,
  }
}
